import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { brand } from '../../config/brand';
import {
  Check,
  X,
  Rocket,
  Zap,
  ShieldCheck,
  Sparkles,
  Trophy,
  Flame,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import LandingNavbar from '../../components/LandingNavbar';

interface TierPricing {
  monthly: number;
  yearly: number;
  trialDurationDays?: number;
  extraSlotPrice?: number;
  polarPriceIdMonthly?: string;
  polarPriceIdYearly?: string;
}

interface TierFeature {
  label: string;
  included: boolean;
  limit?: string;
}

interface TierConfig {
  name: string;
  pricing: TierPricing;
  features: TierFeature[];
  description: string;
  popular?: boolean;
}

export default function PricingPage(): JSX.Element {
  const history = useHistory();
  const token = localStorage.getItem('token');
  const { user } = useGlobalState();
  const [tiers, setTiers] = useState<Record<string, TierConfig>>({});
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [loading, setLoading] = useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState<{ polarPriceId?: string | null } | null>(null);
  
  // If subscription is cancelled, treat as if no active subscription for button display
  const isCancelled = user?.subscriptionStatus === 'cancelled' || user?.subscriptionStatus === 'expired';

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await axios.get('/api/subscriptions/tiers');
        // Add descriptions and names on frontend for now, or fetch from backend if possible
        const data = response.data;
        const enhancedTiers = {
          BASIC: {
            ...data.BASIC,
            name: brand.planNames.basic,
            description: "Essential tools for getting started."
          },
          PRO: {
            ...data.PRO,
            name: brand.planNames.pro,
            description: "Advanced tools to maximize your reach.",
            popular: true
          },
          AGENCY: {
            ...data.AGENCY,
            name: brand.planNames.agency,
            description: "Full power tools for teams managing multiple brands. No human services."
          }
        };
        setTiers(enhancedTiers);
      } catch (error) {
        console.error("Error fetching tiers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
  }, []);

  const getSavings = (monthly: number, yearly: number) => {
    const monthlyTotal = monthly * 12;
    const yearlyTotal = yearly * 12;
    const savings = monthlyTotal - yearlyTotal;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    return { savings, percentage };
  };

  const handleSubscribe = async (polarPriceId?: string | null) => {
    if (polarPriceId) {
      localStorage.setItem('polar_checkout_intent', polarPriceId);
    }

    if (!token) {
      history.push('/login');
      return;
    }

    if (!polarPriceId) {
      history.push('/dashboard');
      return;
    }

    // Check if user has remaining time on cancelled/expired subscription
    if (user && isCancelled && user.subscriptionEndsAt) {
      const endsAt = new Date(user.subscriptionEndsAt);
      const now = new Date();
      if (endsAt > now) {
        setPendingCheckout({ polarPriceId });
        setShowWarningModal(true);
        return;
      }
    }

    // Proceed with checkout
    try {
      setLoading(true);
      const response = await axios.post('/api/subscriptions/checkout', { polarPriceId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }
      setLoading(false);
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert("Failed to initiate checkout. Please try again.");
      setLoading(false);
    }
  };

  const handleConfirmCheckout = async () => {
    setShowWarningModal(false);
    if (!pendingCheckout) return;

    try {
      setLoading(true);
      const response = await axios.post('/api/subscriptions/checkout', {
        polarPriceId: pendingCheckout.polarPriceId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }
      setLoading(false);
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert("Failed to initiate checkout. Please try again.");
      setLoading(false);
    }
  };

  const calculateRemainingDays = () => {
    if (!user?.subscriptionEndsAt) return 0;
    const endsAt = new Date(user.subscriptionEndsAt);
    const now = new Date();
    const diffMs = endsAt.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full animate-pulse" />
          <Loader2 className="h-16 w-16 text-primary animate-spin relative z-10" />
        </div>
        <div className="space-y-2 text-center relative z-10">
          <h2 className="text-xl font-black text-foreground uppercase tracking-tighter italic">Loading pricing plans</h2>
          <p className="text-muted-foreground text-sm font-bold uppercase tracking-widest animate-pulse">Please wait a moment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30">
      <LandingNavbar />

      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute bottom-[10%] right-[-10%] w-[30%] h-[30%] bg-indigo-600/10 blur-[120px] rounded-full animate-pulse delay-700" />
        </div>

        <div className="container mx-auto px-4 text-center">
          <div className="mb-12">
            <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-1 mb-6">
              <Flame className="h-3.5 w-3.5 mr-2 fill-primary" />
              SIMPLE, TRANSPARENT PRICING
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black text-foreground tracking-tighter mb-6 leading-none italic">
              SCALABLE PLANS FOR<br />YOUR <span className="text-primary">STUDIO</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-medium leading-relaxed">
              Choose the plan that fits your studio's needs. No hidden fees, cancel anytime.
            </p>
          </div>

          <div className="flex items-center justify-center gap-6 mb-16">
            <div className="flex items-center gap-3 bg-card/50 p-1.5 rounded-3xl border border-border/50 backdrop-blur-xl">
              <div className="flex items-center gap-2 px-3">
                <Label className={billingCycle === 'monthly' ? 'text-foreground font-bold' : 'text-muted-foreground'}>Monthly</Label>
              </div>
              <Switch
                checked={billingCycle === 'yearly'}
                onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
                className="data-[state=checked]:bg-primary"
              />
              <div className="flex items-center gap-2 px-3">
                <Label className={billingCycle === 'yearly' ? 'text-foreground font-bold' : 'text-muted-foreground'}>Yearly</Label>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/20 font-black animate-pulse uppercase tracking-widest text-[10px]">3 Months Free</Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto items-stretch">
            {Object.keys(tiers).map((key) => {
              const plan = tiers[key];
              const isPro = key === 'PRO';
              const isAgency = key === 'AGENCY';
              const price = billingCycle === 'monthly' ? plan.pricing.monthly : plan.pricing.yearly;
              const { percentage } = getSavings(plan.pricing.monthly, plan.pricing.yearly);

              return (
                <div key={key} className={`relative flex flex-col group h-full ${isPro ? 'z-10' : 'z-0'}`}>
                  {plan.popular && (
                    <div className="absolute -top-4 inset-x-0 flex justify-center z-20">
                      <Badge className="bg-primary text-primary-foreground border-none px-6 py-1.5 rounded-full font-black text-[10px] tracking-widest shadow-xl animate-bounce">
                        MOST POPULAR
                      </Badge>
                    </div>
                  )}

                  <Card
                    className={`flex flex-col h-full bg-card/50 border-border transition-all duration-500 backdrop-blur-xl rounded-[40px] overflow-hidden ${isPro
                      ? 'scale-105 border-primary/50 shadow-[0_0_50px_-12px_hsl(var(--primary)/0.3)] animate-rainbow-border'
                      : 'hover:border-border hover:bg-card/50'
                      }`}
                  >
                    <CardHeader className="p-10 pb-6 text-left">
                      <div className="flex justify-between items-start mb-4">
                        <div className={`p-3 rounded-2xl ${isPro ? 'bg-primary text-primary-foreground' : 'bg-card text-primary'}`}>
                          {isAgency ? <Trophy className="h-6 w-6" /> : (isPro ? <Zap className="h-6 w-6" /> : <Rocket className="h-6 w-6" />)}
                        </div>
                      </div>

                      <CardTitle className="text-3xl font-black text-foreground uppercase tracking-tighter mb-2 italic">
                        {plan.name}
                      </CardTitle>

                      <div className="mt-4 flex flex-col">
                        <div className="flex items-baseline gap-1">
                          {isAgency ? (
                            <span className="text-4xl font-black text-foreground">Custom</span>
                          ) : (
                            <>
                              <span className="text-5xl font-black text-foreground animate-in slide-in-from-bottom-2">
                                {price === 0 ? "TBD" : `$${price}`}
                              </span>
                              {price > 0 && (
                                <span className="text-muted-foreground font-bold uppercase tracking-widest text-xs">/ month</span>
                              )}
                            </>
                          )}
                        </div>
                        {billingCycle === 'yearly' && price > 0 && (
                          <div className="text-[11px] font-bold text-emerald-400 mt-1 uppercase tracking-wider">
                            ${price * 12} billed annually — you get 3 months on us!
                          </div>
                        )}
                        {billingCycle === 'monthly' && price > 0 && key !== 'BASIC' && (
                          <div className="text-[11px] font-bold text-muted-foreground mt-1 uppercase tracking-wider">
                            Better value with yearly billing (-{percentage}%)
                          </div>
                        )}
                        {isPro && plan.pricing.trialDurationDays && (
                          <div className="mt-3 bg-primary/10 border border-primary/30 p-3 rounded-2xl flex items-center gap-3">
                            <div className="bg-primary rounded-lg p-1.5 shrink-0">
                              <Sparkles className="h-3 w-3 text-primary-foreground" />
                            </div>
                            <span className="text-[10px] text-primary font-black uppercase leading-tight">
                              {plan.pricing.trialDurationDays}-day free trial · No charge until trial ends
                            </span>
                          </div>
                        )}
                      </div>

                      <p className="mt-6 text-muted-foreground text-sm font-medium leading-relaxed min-h-[40px]">
                        {plan.description}
                      </p>
                    </CardHeader>

                    <CardContent className="p-10 pt-0 flex-1">
                      <div className="space-y-4 pt-4 border-t border-border">
                        {plan.features.map((feature, fidx) => (
                          <div key={fidx} className={`flex items-center gap-3 transition-opacity duration-300 ${feature.included ? 'opacity-100' : 'opacity-70'}`}>
                            {feature.included ? (
                              <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                            ) : (
                              <div className="h-5 w-5 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 border border-red-500/20">
                                <X className="h-3 w-3 text-red-500" />
                              </div>
                            )}
                            <span className={`text-sm font-medium ${feature.included ? 'text-foreground' : 'text-foreground'}`}>
                              {feature.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>

                    <CardFooter className="p-10 pt-0">
                      <Button
                        onClick={() => isAgency
                          ? (window.location.href = `mailto:${brand.email}`)
                          : handleSubscribe(
                              billingCycle === 'yearly' ? plan.pricing.polarPriceIdYearly : plan.pricing.polarPriceIdMonthly
                            )}
                        disabled={
                          !!(token && user && !isCancelled && (
                            (key === 'BASIC' && user.tier === 'BASIC') ||
                            (key === 'PRO' && user.tier === 'PRO') ||
                            (key === 'AGENCY' && user.tier === 'AGENCY')
                          ))
                        }
                        className={`w-full h-16 rounded-[24px] font-black uppercase tracking-widest text-sm transition-all shadow-2xl ${isPro
                          ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-primary/30 hover:scale-[1.02] active:scale-95 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:hover:scale-100'
                          : isAgency
                            ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/30 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed'
                            : 'bg-card hover:bg-muted text-foreground border border-border active:scale-95 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:hover:bg-muted'
                          }`}>
                        {isAgency ? (
                          "Contact Us"
                        ) : token && user && !isCancelled ? (
                          // User is logged in with ACTIVE subscription - show context-aware text
                          key === 'BASIC' ? (
                            user.tier === 'BASIC' ? 'Already on Basic' :
                            user.tier === 'PRO' ? 'Downgrade to Basic' :
                            user.tier === 'AGENCY' ? 'Already on Agency' :
                            'Select Plan'
                          ) : key === 'PRO' ? (
                            user.tier === 'BASIC' ? `Upgrade to ${brand.planNames.pro}` :
                            user.tier === 'PRO' ? `Already on ${brand.planNames.pro}` :
                            user.tier === 'AGENCY' ? 'Already on Agency' :
                            'Select Plan'
                          ) : (
                            'Select Plan'
                          )
                        ) : token ? (
                          // User logged in but cancelled or no subscription - treat as free
                          key === 'BASIC' ? "Get Started" :
                          key === 'PRO' ? "Start Free Trial" : "Start Publishing"
                        ) : (
                          // User not logged in
                          key === 'BASIC' ? "Get Started" :
                          key === 'PRO' ? "Start Free Trial" : "Start Publishing"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Trust section */}
      <section className="py-20 border-t border-border/50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] mb-12">Trusted by content creators worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-24 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8" />
              <span className="text-xl font-black italic tracking-tighter">SECURE</span>
            </div>
            <div className="flex items-center gap-3">
              <Zap className="h-8 w-8" />
              <span className="text-xl font-black italic tracking-tighter">FAST</span>
            </div>
            <div className="flex items-center gap-3">
              <Trophy className="h-8 w-8" />
              <span className="text-xl font-black italic tracking-tighter">SUCCESS</span>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Modal */}
      <Dialog open={showWarningModal} onOpenChange={setShowWarningModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-6 w-6 text-yellow-500" />
              <DialogTitle>Important: Remaining Time</DialogTitle>
            </div>
            <DialogDescription className="text-base pt-2">
              You currently have <span className="font-bold text-foreground">{calculateRemainingDays()} days</span> remaining on your current plan.
              <br /><br />
              If you continue with this purchase, <span className="font-bold text-yellow-500">you will lose those {calculateRemainingDays()} days</span> and your new subscription will start today.
              <br /><br />
              Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowWarningModal(false);
                setPendingCheckout(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCheckout}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Yes, Continue'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

